import { captureError } from './sentry';

let loadPromise: Promise<void> | null = null;

// Ride out a couple of automatic attempts before requiring a manual Retry click.
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [1000, 2000];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wait for the SDK's internal iframe to finish loading.
 * The SDK creates a hidden iframe on script parse and stores the resolved
 * promise under a Symbol key. We can't access that directly, so we probe
 * by attempting a lightweight VideoClient instantiation. If the SDK's
 * internal state isn't ready yet, the constructor or init throws.
 * Poll at short intervals for up to 5 seconds.
 */
function waitForSdkReady(timeout = 5000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      if (window.VideoClient) {
        resolve();
        return;
      }
      if (Date.now() - start > timeout) {
        reject(new Error('Zoom SDK did not become ready within timeout'));
        return;
      }
      setTimeout(check, 100);
    };

    check();
  });
}

// Removes its own <script> tag on failure so a retry doesn't accumulate duplicates.
function attemptLoad(apiKey: string, env: string, attempt: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (window.VideoClient) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://${env}ccistatic.zoom.us/${env}cci/web-sdk/zcc-sdk.js`;
    script.setAttribute('data-apikey', apiKey);
    script.setAttribute('data-env', env);
    script.async = true;

    script.onload = () => {
      waitForSdkReady()
        .then(resolve)
        .catch((err) => {
          captureError(err, 'zoom_sdk_load', { reason: 'sdk_not_ready_timeout', attempt: String(attempt) });
          script.remove();
          reject(err);
        });
    };
    script.onerror = () => {
      const err = new Error('Failed to load Zoom CC SDK script');
      captureError(err, 'zoom_sdk_load', { reason: 'script_error', attempt: String(attempt) });
      script.remove();
      reject(err);
    };

    document.head.appendChild(script);
  });
}

export function loadZoomSdk(): Promise<void> {
  if (loadPromise) return loadPromise;

  const apiKey = process.env.REACT_APP_ZOOM_API_KEY;
  const env = process.env.REACT_APP_ZOOM_ENV;

  if (!apiKey || !env) {
    const err = new Error('Zoom SDK configuration missing: REACT_APP_ZOOM_API_KEY and REACT_APP_ZOOM_ENV are required');
    captureError(err, 'zoom_sdk_load', { reason: 'missing_config' });
    return Promise.reject(err);
  }

  const runAttempts = async (): Promise<void> => {
    for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
      try {
        await attemptLoad(apiKey, env, attempt);
        return;
      } catch (err) {
        if (attempt === MAX_LOAD_ATTEMPTS) {
          // Reset so a later manual retry starts a fresh sequence, not a replay of this rejection.
          loadPromise = null;
          throw err;
        }
        await delay(RETRY_DELAYS_MS[attempt - 1]);
      }
    }
  };

  loadPromise = runAttempts();
  return loadPromise;
}
